import React, { useState, useEffect } from 'react';
import {
  FiMapPin, FiPlus, FiEdit2, FiTrash2, FiStar, FiCheck, FiX,
  FiHome, FiBriefcase, FiHeart, FiTag,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import addressService from '../../services/address.service';

const LABEL_ICONS = { Home: FiHome, Work: FiBriefcase, Family: FiHeart };
const LABEL_OPTIONS = ['Home', 'Work', 'Family', 'Other'];

const emptyAddr = {
  label: 'Home', fullName: '', phone: '', street: '', apartment: '',
  city: '', state: '', zip: '', country: 'US', isDefault: false,
};

/**
 * AddressBook
 *
 * Props:
 *   mode         - 'manage' (full CRUD, for profile) | 'select' (pick address, for checkout)
 *   selectedId   - currently selected address _id (select mode)
 *   onSelect     - callback(address) when user picks an address (select mode)
 *   compact      - if true, shows a compact view
 */
const AddressBook = ({ mode = 'manage', selectedId, onSelect, compact = false }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyAddr });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAddresses(); }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await addressService.getAll();
      setAddresses(res.data.data || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyAddr });
    setShowForm(true);
  };

  const openEdit = (addr) => {
    setEditing(addr._id);
    setForm({
      label: addr.label || 'Home',
      fullName: addr.fullName || '',
      phone: addr.phone || '',
      street: addr.street || '',
      apartment: addr.apartment || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      country: addr.country || 'US',
      isDefault: addr.isDefault || false,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.street || !form.city || !form.state || !form.zip) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await addressService.update(editing, form);
        toast.success('Address updated');
      } else {
        await addressService.create(form);
        toast.success('Address added');
      }
      setShowForm(false);
      setEditing(null);
      fetchAddresses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await addressService.remove(id);
      toast.success('Address deleted');
      fetchAddresses();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await addressService.setDefault(id);
      toast.success('Default address updated');
      fetchAddresses();
    } catch { toast.error('Failed to set default'); }
  };

  const LabelIcon = LABEL_ICONS[form.label] || FiTag;

  // ── RENDER ──
  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 style={{ fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
          <FiMapPin size={18} color="#B91C1C" />
          {mode === 'select' ? 'Select Delivery Address' : 'Address Book'}
        </h5>
        <button onClick={openAdd} className="btn btn-sm btn-danger" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FiPlus size={14} /> Add New
        </button>
      </div>

      {/* Loading */}
      {loading && <div className="text-center py-4 text-muted">Loading addresses...</div>}

      {/* Empty state */}
      {!loading && addresses.length === 0 && !showForm && (
        <div className="text-center py-5" style={{ background: '#FAFAFA', borderRadius: '12px', border: '2px dashed #E5E7EB' }}>
          <FiMapPin size={36} style={{ color: '#D1D5DB', marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, color: '#6B7280', margin: '0 0 4px' }}>No saved addresses</p>
          <p style={{ fontSize: '13px', color: '#9CA3AF', margin: '0 0 12px' }}>Add your first delivery address</p>
          <button onClick={openAdd} className="btn btn-sm btn-outline-danger"><FiPlus size={14} className="me-1" /> Add Address</button>
        </div>
      )}

      {/* Address Cards */}
      {!loading && addresses.length > 0 && (
        <div className="row g-3">
          {addresses.map(addr => {
            const AddrIcon = LABEL_ICONS[addr.label] || FiTag;
            const isSelected = selectedId === addr._id;
            return (
              <div key={addr._id} className={compact ? 'col-12' : 'col-md-6'}>
                <div
                  onClick={() => mode === 'select' && onSelect?.(addr)}
                  style={{
                    border: isSelected ? '2px solid #B91C1C' : '1px solid #E5E7EB',
                    borderRadius: '12px', padding: '16px', cursor: mode === 'select' ? 'pointer' : 'default',
                    background: isSelected ? '#FEF2F2' : '#FFF',
                    transition: 'all 0.2s', position: 'relative',
                  }}
                >
                  {/* Label + Default badge */}
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AddrIcon size={14} color="#B91C1C" />
                      <span style={{ fontWeight: 700, fontSize: '13px', color: '#B91C1C' }}>{addr.label}</span>
                      {addr.isDefault && (
                        <span style={{
                          background: '#ECFDF5', color: '#059669', fontSize: '10px', fontWeight: 700,
                          padding: '2px 6px', borderRadius: '6px',
                        }}>DEFAULT</span>
                      )}
                    </div>
                    {mode === 'select' && isSelected && (
                      <FiCheck size={18} color="#B91C1C" strokeWidth={3} />
                    )}
                  </div>

                  {/* Address text */}
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>{addr.fullName}</div>
                  <div style={{ fontSize: '13px', color: '#4B5563', lineHeight: 1.6 }}>
                    {addr.street}{addr.apartment ? `, ${addr.apartment}` : ''}<br />
                    {addr.city}, {addr.state} {addr.zip}<br />
                    {addr.country}
                    {addr.phone && <><br />📞 {addr.phone}</>}
                  </div>

                  {/* Actions */}
                  {mode === 'manage' && (
                    <div className="d-flex gap-2 mt-3">
                      <button onClick={() => openEdit(addr)} className="btn btn-sm btn-outline-secondary" style={{ fontSize: '12px' }}>
                        <FiEdit2 size={12} className="me-1" /> Edit
                      </button>
                      {!addr.isDefault && (
                        <button onClick={() => handleSetDefault(addr._id)} className="btn btn-sm btn-outline-success" style={{ fontSize: '12px' }}>
                          <FiStar size={12} className="me-1" /> Set Default
                        </button>
                      )}
                      <button onClick={() => handleDelete(addr._id)} className="btn btn-sm btn-outline-danger" style={{ fontSize: '12px' }}>
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Form (Modal-like inline) */}
      {showForm && (
        <div style={{
          marginTop: '16px', border: '1px solid #E5E7EB', borderRadius: '12px',
          padding: '20px', background: '#FAFAFA',
        }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 style={{ fontWeight: 700, margin: 0 }}>{editing ? 'Edit Address' : 'Add New Address'}</h6>
            <button onClick={() => { setShowForm(false); setEditing(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
              <FiX size={18} />
            </button>
          </div>

          <form onSubmit={handleSave}>
            {/* Label pills */}
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Label</label>
              <div className="d-flex gap-2 flex-wrap">
                {LABEL_OPTIONS.map(l => (
                  <button key={l} type="button"
                    className={`btn btn-sm ${form.label === l ? 'btn-danger' : 'btn-outline-secondary'}`}
                    onClick={() => setForm({ ...form, label: l })}
                    style={{ fontSize: '12px' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: '13px' }}>Full Name *</label>
                <input className="form-control form-control-sm" value={form.fullName}
                  onChange={e => setForm({ ...form, fullName: e.target.value })} required />
              </div>
              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: '13px' }}>Phone</label>
                <input className="form-control form-control-sm" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="col-12">
                <label className="form-label" style={{ fontSize: '13px' }}>Street Address *</label>
                <input className="form-control form-control-sm" value={form.street}
                  onChange={e => setForm({ ...form, street: e.target.value })} required />
              </div>
              <div className="col-12">
                <label className="form-label" style={{ fontSize: '13px' }}>Apartment / Suite / Floor</label>
                <input className="form-control form-control-sm" value={form.apartment}
                  onChange={e => setForm({ ...form, apartment: e.target.value })} placeholder="Optional" />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '13px' }}>City *</label>
                <input className="form-control form-control-sm" value={form.city}
                  onChange={e => setForm({ ...form, city: e.target.value })} required />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '13px' }}>State *</label>
                <input className="form-control form-control-sm" value={form.state}
                  onChange={e => setForm({ ...form, state: e.target.value })} required />
              </div>
              <div className="col-md-4">
                <label className="form-label" style={{ fontSize: '13px' }}>ZIP Code *</label>
                <input className="form-control form-control-sm" value={form.zip}
                  onChange={e => setForm({ ...form, zip: e.target.value })} required />
              </div>
              <div className="col-md-6">
                <label className="form-label" style={{ fontSize: '13px' }}>Country</label>
                <select className="form-select form-select-sm" value={form.country}
                  onChange={e => setForm({ ...form, country: e.target.value })}>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="IN">India</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="makeDefault"
                    checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
                  <label className="form-check-label" htmlFor="makeDefault" style={{ fontSize: '13px' }}>
                    Set as default address
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn btn-sm btn-danger" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Address' : 'Save Address')}
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => { setShowForm(false); setEditing(null); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddressBook;
